import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, Zap, Trophy } from "lucide-react";
import useGameTick from "@/lib/useGameTick";
import ArcadeScoreBar from "./ArcadeScoreBar";

const PAY_TABLE = [
  { combo: "7️⃣ 7️⃣ 7️⃣", x: "50×", vibe: "JACKPOT" },
  { combo: "💎 💎 💎", x: "25×", vibe: "MYTHIC"  },
  { combo: "⭐ ⭐ ⭐", x: "15×", vibe: "LEGENDARY" },
  { combo: "🔔 🔔 🔔", x: "10×", vibe: "EPIC" },
  { combo: "🍋 🍋 🍋", x: "6×",  vibe: "RARE" },
  { combo: "🍒 🍒 🍒", x: "5×",  vibe: "RARE" },
  { combo: "Any pair", x: "2×",  vibe: "TWO IN A ROW" },
];

export default function Slots() {
  const { user } = useAuth();
  useGameTick("slots"); // +1 Spark / min while playing
  const [reels, setReels] = useState(["🍒", "🔔", "💎"]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/arcade/my-best").then(r => setBestScore(r.data?.slots?.score || 0)).catch(() => {});
  }, [user?.username]);

  const spin = async () => {
    if (!user) { setErr("Log in to play"); return; }
    setErr(""); setSpinning(true); setResult(null);
    const interval = setInterval(() => {
      const pool = ["🍒", "🍋", "🔔", "💎", "⭐", "7️⃣"];
      setReels([
        pool[Math.floor(Math.random() * pool.length)],
        pool[Math.floor(Math.random() * pool.length)],
        pool[Math.floor(Math.random() * pool.length)],
      ]);
    }, 70);
    try {
      const r = await api.post("/games/slots/spin", { bet: 0 });
      setTimeout(() => {
        clearInterval(interval);
        setReels(r.data.reels);
        setResult(r.data);
        setSpinning(false);
        if (r.data.won) {
          const newStreak = streak + 1;
          setStreak(newStreak);
          const winPoints = (r.data.fun_score || 0) * 10; // multiplier → score points
          const nextSession = sessionScore + winPoints;
          setSessionScore(nextSession);
          if (nextSession > bestScore && user) {
            api.post("/arcade/score", { game_slug: "slots", score: nextSession })
              .then(res => {
                if (res.data.new_best) setBestScore(res.data.best);
                setRefreshKey(k => k + 1);
              })
              .catch(() => {});
          }
        } else {
          setStreak(0);
        }
      }, 900);
    } catch (e) {
      clearInterval(interval);
      setSpinning(false);
      setErr(e?.response?.data?.detail || "Spin failed");
    }
  };

  return (
    <div data-testid="game-slots" className="bg-black/60 border border-white/10 rounded-md p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="sc-heading text-2xl font-bold">MystiCast Slots</div>
          <div className="text-xs text-zinc-400 mt-1">Free play · No wager · No payout · Pure fun.</div>
        </div>
        {user && (
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-zinc-400 flex items-center gap-1 justify-end"><Trophy className="w-3 h-3" /> This session</div>
            <div className="text-lg font-bold text-[color:var(--sc-cyan)]" data-testid="slots-session-score">{sessionScore}</div>
          </div>
        )}
      </div>

      {/* Reels */}
      <div className="bg-gradient-to-b from-[#0c0c0e] to-[#18181B] border-2 border-[color:var(--sc-cyan)]/30 rounded-md p-6 my-5">
        <div className="grid grid-cols-3 gap-3">
          {reels.map((s, i) => (
            <div
              key={i}
              data-testid={`slots-reel-${i}`}
              className="bg-[#0c0c0e] border border-white/10 rounded-sm text-6xl flex items-center justify-center aspect-square"
              style={{ filter: spinning ? "blur(2px)" : "none", transition: "filter 0.2s" }}
            >
              {s}
            </div>
          ))}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`text-center mb-4 font-bold ${result.won ? "text-[color:var(--sc-volt)]" : "text-zinc-500"}`} data-testid="slots-result">
          {result.won
            ? `🎉 ${result.fun_score}× match! Streak: ${streak}`
            : `No match — spin again`}
        </div>
      )}
      {err && <div className="text-[#FF3366] text-center mb-3 text-sm">{err}</div>}

      <button data-testid="slots-spin" disabled={spinning} onClick={spin} className="sc-btn w-full flex items-center justify-center gap-2">
        <Sparkles className="w-4 h-4" /> {spinning ? "Spinning..." : "Spin"}
      </button>

      <div className="text-center mt-3 text-[11px] text-zinc-500 flex items-center justify-center gap-1">
        <Zap className="w-3 h-3 text-[color:var(--sc-cyan)]" />
        <span className="text-[color:var(--sc-cyan)] font-bold">+1 Spark / min</span> while actively playing · idle doesn&apos;t count.
      </div>

      {/* Paytable */}
      <div className="mt-6 border border-white/5 rounded-sm">
        <div className="px-3 py-2 border-b border-white/5 text-xs sc-label" style={{ color: "var(--sc-cyan)" }}>Fun-score Table</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 text-xs">
          {PAY_TABLE.map((p) => (
            <div key={p.combo} className="px-3 py-2 border-b border-r border-white/5 last:border-b-0 flex items-center justify-between">
              <span>{p.combo}</span><span className="text-[color:var(--sc-volt)] font-bold">{p.x}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[10px] text-zinc-500 mt-4 text-center">
        Free-to-play entertainment. No currency wagered, no currency awarded, no cash value. This is not a gambling product.
      </div>

      <ArcadeScoreBar gameSlug="slots" refreshKey={refreshKey} />
    </div>
  );
}
